import { isApplicationBuilderProposal } from "@lib/applicationProposalDisplay";
import type { RoundAggregate } from "@engine/round/aggregate";
import { getActiveProposals } from "@engine/round/accessors";

type ProposalRouteInfo = {
  shortname: string;
};

export function getProposalRouteSegment(proposal: ProposalRouteInfo): string {
  return encodeURIComponent(proposal.shortname).replaceAll("%40", "@");
}

export function getRoundProposalPath(
  roundId: string,
  proposal: ProposalRouteInfo,
): string {
  return `/round/${roundId}/proposals/${getProposalRouteSegment(proposal)}`;
}

/**
 * The proposal page's All Notes tab, optionally scrolled to (and highlighting)
 * one evaluator's notes. `evaluatorMemberId` is a round-member id — the same
 * value the tab reads from `?evaluator=` and stamps as `data-evaluator-id`.
 */
export function getRoundProposalNotesPath(
  roundId: string,
  proposal: ProposalRouteInfo,
  evaluatorMemberId?: string | null,
): string {
  const params = new URLSearchParams({ tab: "all-notes" });
  if (evaluatorMemberId) {
    params.set("evaluator", evaluatorMemberId);
  }
  return `${getRoundProposalPath(roundId, proposal)}?${params}`;
}

function decodeRouteSegment(routeSegment: string): string {
  try {
    return decodeURIComponent(routeSegment);
  } catch {
    return routeSegment;
  }
}

export function resolveProposalIdFromRouteSegment(
  round: RoundAggregate,
  routeSegment: string,
): string | null {
  const routeSegmentCandidates = [
    ...new Set([routeSegment, decodeRouteSegment(routeSegment)]),
  ];
  const readableProposals = [
    ...getActiveProposals(round),
    ...Object.entries(round.proposalForms)
      .filter(([, form]) => form.status === "active")
      .flatMap(([formId, form]) =>
        Object.values(form.proposals).filter(
          (proposal) =>
            proposal.status === "withdrawn" &&
            isApplicationBuilderProposal(proposal, formId),
        ),
      ),
  ];

  for (const candidate of routeSegmentCandidates) {
    const proposalById = readableProposals.find(
      (proposal) => proposal.id === candidate,
    );
    if (proposalById) {
      return proposalById.id;
    }
  }

  const proposalByShortname = readableProposals.find((proposal) =>
    routeSegmentCandidates.includes(proposal.shortname),
  );
  return proposalByShortname?.id ?? null;
}
